import { useMutation, useQueryClient } from "@tanstack/react-query";
import { estimateApi } from "./estimate.api";
import { ESTIMATE_KEYS } from "./estimate.queries";
import { BOOKING_KEYS } from "../booking";
import {
  CreateEstimateDto,
  UpdateEstimateDto,
  ConvertEstimateDto,
  CreateEstimateServiceDto,
  EstimateTicket,
  CreateExpoServiceDaysDto,
  GenerateExpoDaysDto,
  CopyExpoDayDto,
} from "~/types/estimate";

export const useCreateEstimate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEstimateDto) => estimateApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ESTIMATE_KEYS.ESTIMATES] });
    },
  });
};

export const useUpdateEstimate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEstimateDto }) =>
      estimateApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [ESTIMATE_KEYS.ESTIMATES] });
      queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.ESTIMATE(variables.id) });
    },
  });
};

export const useDeleteEstimate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => estimateApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ESTIMATE_KEYS.ESTIMATES] });
    },
  });
};

export const useConvertEstimate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ConvertEstimateDto) => estimateApi.convertToBooking(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [ESTIMATE_KEYS.ESTIMATES] });
      queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.ESTIMATE(variables.estimateId) });
      queryClient.invalidateQueries({ queryKey: [BOOKING_KEYS.BOOKINGS] });
    },
  });
};

// Service mutations
export const useAddEstimateService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEstimateServiceDto) => estimateApi.addService(data),
    onSuccess: async (newService, variables) => {
      console.log("🔄 Cache update starting");
      console.log("📦 New service:", newService);
      console.log("🔑 Estimate ID:", variables.estimateTicketId);
      console.log("🔑 Cache key:", ESTIMATE_KEYS.ESTIMATE(variables.estimateTicketId));

      // Update the cache directly by adding the new service to the estimate
      const updated = queryClient.setQueryData<EstimateTicket>(
        ESTIMATE_KEYS.ESTIMATE(variables.estimateTicketId),
        (oldData) => {
          console.log("📥 Old data:", oldData);
          console.log("📥 Old services:", oldData?.services);

          if (!oldData) {
            console.log("⚠️ No old data!");
            return oldData;
          }

          const newData = {
            ...oldData,
            services: [...(oldData.services || []), newService],
          };

          console.log("📤 New data services:", newData.services);
          return newData;
        }
      );

      console.log("✅ Cache updated, result:", updated);

      // Also invalidate the estimates list so it shows updated service counts
      queryClient.invalidateQueries({
        queryKey: [ESTIMATE_KEYS.ESTIMATES],
      });
    },
  });
};

export const useUpdateEstimateService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceId, data, estimateId }: { serviceId: string; data: Partial<CreateEstimateServiceDto>; estimateId: string }) =>
      estimateApi.updateService(serviceId, data),
    onSuccess: async (updatedService, variables) => {
      // Update the cache directly by replacing the service
      queryClient.setQueryData<EstimateTicket>(
        ESTIMATE_KEYS.ESTIMATE(variables.estimateId),
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            services: oldData.services?.map(s =>
              s.id === updatedService.id ? updatedService : s
            ) || [],
          };
        }
      );

      // Also invalidate the estimates list
      queryClient.invalidateQueries({
        queryKey: [ESTIMATE_KEYS.ESTIMATES],
      });
    },
  });
};

export const useDeleteEstimateService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceId, estimateId }: { serviceId: string; estimateId: string }) =>
      estimateApi.deleteService(serviceId),
    onSuccess: async (_, variables) => {
      // Update the cache directly by removing the service
      queryClient.setQueryData<EstimateTicket>(
        ESTIMATE_KEYS.ESTIMATE(variables.estimateId),
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            services: oldData.services?.filter(s => s.id !== variables.serviceId) || [],
          };
        }
      );

      // Also invalidate the estimates list
      queryClient.invalidateQueries({
        queryKey: [ESTIMATE_KEYS.ESTIMATES],
      });
    },
  });
};

// ============================================
// EXPO MODE MUTATIONS
// ============================================

export const useGenerateExpoDays = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GenerateExpoDaysDto) => estimateApi.generateExpoDays(data),
    onSuccess: (_, variables) => {
      // Invalidate the expo summary query
      queryClient.invalidateQueries({
        queryKey: ESTIMATE_KEYS.EXPO_SUMMARY(variables.estimateServiceId),
      });
    },
  });
};

export const useSaveExpoDays = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateExpoServiceDaysDto) => estimateApi.saveExpoDays(data),
    onSuccess: (result, variables) => {
      // Update the expo summary cache
      queryClient.setQueryData(
        ESTIMATE_KEYS.EXPO_SUMMARY(variables.estimateServiceId),
        result
      );
    },
  });
};

export const useCopyExpoDay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CopyExpoDayDto) => estimateApi.copyExpoDay(data),
    onSuccess: (result, variables) => {
      // Update the expo summary cache
      queryClient.setQueryData(
        ESTIMATE_KEYS.EXPO_SUMMARY(variables.estimateServiceId),
        result
      );
    },
  });
};

export const useDeleteExpoDay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dayId, estimateServiceId }: { dayId: string; estimateServiceId: string }) =>
      estimateApi.deleteExpoDay(dayId),
    onSuccess: (_, variables) => {
      // Invalidate the expo summary query
      queryClient.invalidateQueries({
        queryKey: ESTIMATE_KEYS.EXPO_SUMMARY(variables.estimateServiceId),
      });
    },
  });
};
